/**
 * [CCI-15904] Meta key carried on a ProseMirror transaction that performs the final AI
 * streaming transformation (i.e. the commit of a single tool-call's content into the doc).
 *
 * The payload allows downstream plugins to:
 *   - know the exact doc range that was replaced (start/end positions in the new doc),
 *   - replay the change (newSlice) for "redo",
 *   - revert the change (originalUserContentSlice) for "undo",
 * without needing to inspect the transaction step-by-step.
 *
 * Set by `buildCommitTransaction` (only when the New AIFC Editor Experience gate is ON).
 * Consumed by the `aiSessionContextPlugin` to maintain `aiContentPositions`.
 */
import type { Slice } from '@atlaskit/editor-prosemirror/model';

import type { SegmentKind } from './types';

export const AI_FINAL_STREAMING_TRANSFORMATION_DATA = 'aiFinalStreamingTransformationData';

export interface AIFinalStreamingTransformationData {
	/**
	 * [CCI-17297] Optional kind for the recorded entry. Streamable commits leave
	 * this undefined (the segment is treated as a generic `update`); it exists so
	 * the consumer can carry a kind uniformly with the non-streamable path.
	 */
	kind?: SegmentKind;
	newSlice: Slice;
	originalUserContentSlice: Slice;
	positions: {
		mappedStartPos: number;
		mappedToPos: number;
	};
}

/**
 * [CCI-17297] Meta carried on a transaction produced by a NON-STREAMABLE command
 * handler (e.g. removeNode, setAttrs, move, wrap) in the streaming orchestrator.
 *
 * Non-streamable commands mutate the document directly in their handler and never
 * reach `buildCommitTransaction`'s slice-capture path, so they would otherwise
 * record no reviewable segment. The handler stamps this meta (via the
 * `recordAIContentSegments` ai-experience action) with one coarse entry per
 * change, and `aiSessionContextPlugin.apply()` appends them to
 * `aiContentPositions` exactly like the streamable path.
 *
 * Each entry follows the same contract as a streamable commit entry: undo
 * replaces `[startPos, endPos]` with `originalUserContentSlice`, redo with
 * `newSlice`.
 */
export const AI_CONTENT_SEGMENT_DATA = 'aiContentSegmentData';

export interface AIContentSegmentEntry {
	endPos: number;
	kind?: SegmentKind;
	newSlice: Slice;
	originalUserContentSlice: Slice;
	startPos: number;
}

export interface AIContentSegmentData {
	segments: AIContentSegmentEntry[];
}

/**
 * Meta on the terminal commit of a tool call force-finished by the queue timeout.
 * Set by `buildCommitTransaction` and consumed by `aiSessionContextPlugin.apply()`,
 * which records the final positions in `abortedToolCallPositions` for resume.
 */
export const CAPTURE_ABORTED_TOOL_CALL_POSITIONS = 'captureAbortedToolCallPositions';

export interface CaptureAbortedToolCallPositionsData {
	aiContentEndPos: number;
	startPos: number;
	toolCallId: string;
}
